/**
 * Summary — the review-and-place step, native.
 *
 * Route: /purchase-flow-revamp/summary
 *
 * This step was the last one mounted inside `legacyBridge/`, and the bridge is
 * gone with it. What it was still there for, and where each piece went:
 *
 *   - `<Logistics />`            → `OrderLogisticsSection`, a store-only port.
 *   - the legacy "Your order" box → already suppressed on a v2 cart (it reads
 *     `selectedProducts`, keyed by numeric product ids a v2 cart never carried);
 *     `SummaryOrderReview` is what actually rendered, via a slot.
 *   - the legacy Stripe box      → never rendered here either: it gates on
 *     `usePurchaseFlowOrderMeta().isStripePaymentMethod`, legacy state the native
 *     Billing step does not write. `StripePaymentSection` is the real one.
 *   - `PlaceOrderHandlerPublisher` → `PlaceOrderHandler`, which was already
 *     native on both the invoice and the card path.
 *   - the in-flight guards       → `usePlacementGuards` + `useBeforeUnloadGuard`.
 *   - every `isEmbed` branch     → dead code on this route by construction:
 *     `EmbedConfig` derives `isEmbed` from `useMatch(Path.EMBED_PURCHASE_FLOW)`,
 *     and that route mounts the LEGACY `PurchaseFlow`, so the Propstack embed
 *     never reaches this page and is untouched by its retirement.
 *
 * What the bridge cost, and the reason to be rid of it: it mounted ~20 legacy
 * providers to render two boxes, among them `MeasurementsContextProvider`, whose
 * `useMeasurements` fires the v1 `GET /order/configuration/measurement/pricing`
 * rate card on entry — on EVERY cart, a photo-only one included, long after the
 * v2 session-scoped endpoints replaced it. No native module read
 * that context; the request existed only because the provider was mounted.
 *
 * `SavedStripePaymentMethodsContextProvider` is the one provider that had to
 * come along: it is a platform context (not legacy PF), it serves the saved
 * cards / SEPA mandates the payment box offers, and the bridge is where it used
 * to be mounted for this tree. `autoFetch` mirrors what the bridge passed.
 */

import { type FC } from 'react'
import Stack from '@mui/material/Stack'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTranslation } from 'react-i18next'

import { useAuth0 } from 'utils/auth'
import { SavedStripePaymentMethodsContextProvider } from 'components/contexts/SavedStripePaymentMethodsContext'

import { useSyncStepWithRoute } from '../../routing/useSyncStepWithRoute'
import { useRequireActiveSession } from '../../routing/useRequireActiveSession'
import { useBeforeUnloadGuard } from '../../routing/useBeforeUnloadGuard'
import { PfPageHeading, StepShell } from '../../components'
import { PF_CART_HIDDEN_QUERY } from '../../components/StepShell.component'
import { RevampPaymentIntentProvider, StripePaymentSection } from './RevampPaymentIntent.provider'
import { SummaryOrderReview } from './SummaryOrderReview.component'
import { OrderLogisticsSection } from './sections'
import { PlaceOrderHandler } from './PlaceOrderHandler'
import { usePlacementGuards } from './usePlacementGuards'
import { PfStepPath, usePfPath } from '../../routing/pfBasePath'
import { EmbedConsentCheckbox } from 'components/common/Embed/EmbedConsent'
import { usePfEmbedConsentState } from '../../routing/usePfEmbedConsent'

/**
 * @page Summary
 * Recaps the order and places it.
 *
 * @example
 * // Registered in PurchaseFlowRevamp.page.tsx as:
 * <Route path="summary" element={<Summary />} />
 */
export const Summary: FC = () => {
  const { t } = useTranslation(['purchase_flow_revamp'])
  const { roles } = useAuth0()

  useSyncStepWithRoute('summary')
  const pfPath = usePfPath()
  const { isConsentRequired, isConsentChecked, setIsConsentChecked } = usePfEmbedConsentState()
  useRequireActiveSession(pfPath(PfStepPath.CATALOGUE))
  useBeforeUnloadGuard()
  usePlacementGuards()

  const isMobile = useMediaQuery(PF_CART_HIDDEN_QUERY)

  return (
    <StepShell>
      <PfPageHeading
        title={t('purchase_flow_revamp:summary.page_title')}
        description={t('purchase_flow_revamp:summary.page_subtitle')}
      />

      <SavedStripePaymentMethodsContextProvider autoFetch={!roles.isAdmin}>
        <RevampPaymentIntentProvider>

          <PlaceOrderHandler />

          {/* Side by side when both fit beside the cart rail, the review wrapping below otherwise; stacked on a phone. */}
          <Stack flexDirection={isMobile ? 'column' : 'row'} flexWrap="wrap" justifyContent="space-between" gap={isMobile ? 2 : 4}>

            <OrderLogisticsSection fullWidth={isMobile} />

            <Stack flex="1 1 360px" minWidth={0} gap={4}>

              {/* The measurement info cards (technician call, delivery,
                  cancellation) live INSIDE this canvas, where legacy put them —
                  `OrderReviewSection` renders them off the review lines. */}
              <SummaryOrderReview />

              <StripePaymentSection />

              {/* Inside the CRM embed only, and only while an acceptance is due.
                  Ticking it is what releases Place Order; the recording itself
                  happens on the button. */}
              {isConsentRequired &&
                <EmbedConsentCheckbox checked={isConsentChecked} onChange={setIsConsentChecked} />
              }

            </Stack>

          </Stack>

        </RevampPaymentIntentProvider>
      </SavedStripePaymentMethodsContextProvider>

    </StepShell>
  )
}
